import { useEffect, useState } from 'react';
import createFifoCache from '@src/services/fifoCache';
import { createByteStore } from '@src/services/byteStore';
import {
  canUseWebCodecs,
  decodeChunk,
  fetchRange,
  frameIndexAt,
  normalizeCacheKey,
  readCache,
  readSampleTable,
  sampleIndexAt,
  writeCache,
} from '@src/services/mediaExtractor';
import { THUMBNAIL_SECOND } from '@src/constants/educators';

// The card's box is 196px tall; extra for high-DPI screens.
const FRAME_HEIGHT = 240;

// A card can mount and unmount as views and pages change, and the same project
// shows up on both. Keyed on the object, so presigning does not miss. Holds the
// blob rather than an object URL: the cache drops its oldest entry silently, and
// a url dropped that way could never be revoked.
const frameCache = createFifoCache(24);

const CACHE_KIND = 'card';

// B-frames are stored ahead of the moment they appear, so the frame on screen at
// a given second is not always the last one needed to build it.
const SAMPLE_MARGIN = 4;

/**
 * Fetch and decode the run from the keyframe before `second` through the frame
 * showing at `second`, and hand back that frame's jpeg. Null if it will not
 * decode.
 *
 * Decoding runs forward from a keyframe, which is why the one before is taken
 * rather than the nearest: everything in between is needed as reference, and
 * that run is the whole cost of landing on the exact moment.
 */
const decodeFrameAt = async (videoUrl, table, second, signal) => {
  const { samples, keyTimes, keyframes, track, offsetSec } = table;

  let at = frameIndexAt(keyTimes, second);
  if (keyTimes[at] > second && at > 0) at -= 1;
  const keyframe = keyframes[at];

  // The byte range to ask for: from the keyframe to the end of the last sample
  // the decoder has to see. Offsets climb with decode order but not strictly,
  // so this takes the furthest rather than the last one's.
  const target = sampleIndexAt(samples, second, track.timescale, offsetSec);
  const last = Math.min(target + SAMPLE_MARGIN, samples.length - 1);
  let end = keyframe.offset + keyframe.size;
  for (let i = 0; i <= last; i += 1) {
    const sample = samples[i];
    if (sample.offset >= keyframe.offset)
      end = Math.max(end, sample.offset + sample.size);
  }

  const bytes = await fetchRange(videoUrl, keyframe.offset, end - 1, signal);
  const store = createByteStore();
  store.add(keyframe.offset, new Uint8Array(bytes));

  let picture = null;
  await decodeChunk({
    table,
    store,
    chunk: { start: keyframe.offset, end },
    seconds: [second],
    height: FRAME_HEIGHT,
    signal,
    onFrame: (moment, blob) => {
      picture = blob;
    },
  });
  return picture;
};

/**
 * One frame of a video as an object URL, for a project card's thumbnail.
 *
 * Reads the sample table through HTTP Range, then fetches and decodes the run
 * holding THUMBNAIL_SECOND — the keyframe before it plus the frames in between,
 * which is what lands on the moment itself rather than up to a keyframe gap away
 * from it. Still a fraction of what a <video> element pulls in around a seek.
 *
 * A video shorter than THUMBNAIL_SECOND lands on its last frame.
 *
 * Returns '' while it works and on any failure, which leaves the card grey.
 * Browsers without VideoDecoder (Safari before 16.4, Firefox before 130) land
 * there too — a decorative thumbnail is not worth a second code path.
 *
 * `limitExtracts` is the grid's limiter, shared by every card on it.
 */
const useVideoThumbnail = (videoUrl, limitExtracts) => {
  const [frameUrl, setFrameUrl] = useState('');

  useEffect(() => {
    if (!videoUrl || !canUseWebCodecs()) return undefined;

    const key = normalizeCacheKey(videoUrl);
    let cancelled = false;
    let objectUrl = '';
    const controller = new AbortController();

    // Minted per mount and handed back on unmount, so scrolling past hundreds
    // of cards leaves none of them alive.
    const publish = (blob) => {
      if (cancelled) return;
      frameCache.set(key, blob);
      objectUrl = URL.createObjectURL(blob);
      setFrameUrl(objectUrl);
    };

    const extract = async () => {
      // Runs before the first await, so a cached frame shows without a gap.
      const held = frameCache.get(key);
      if (held) {
        publish(held);
        return;
      }

      try {
        // Survives a reload, so coming back to the home page costs no network.
        // Outside the limiter: cheap, and queueing it would hold back frames
        // already on disk.
        const saved = await readCache(videoUrl, CACHE_KIND);
        if (cancelled) return;
        if (saved?.blob) {
          publish(saved.blob);
          return;
        }

        await limitExtracts(async () => {
          // May have unmounted while queued.
          if (cancelled) return;

          const table = await readSampleTable(videoUrl, controller.signal);
          if (cancelled || !table.keyframes.length) return;

          const picture = await decodeFrameAt(
            videoUrl,
            table,
            THUMBNAIL_SECOND,
            controller.signal,
          );
          if (cancelled || !picture) return;

          publish(picture);
          writeCache(videoUrl, CACHE_KIND, { blob: picture });
        });
      } catch (error) {
        // Unreadable, unsupported codec, or aborted — the card keeps its grey.
      }
    };

    extract();

    return () => {
      cancelled = true;
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [videoUrl, limitExtracts]);

  return frameUrl;
};

export default useVideoThumbnail;
